import { useId } from 'react'
import type { PackageSummary } from '@/lib/packageBillingApi'
import BillingUsageFreshness from './BillingUsageFreshness'
import './packageCostGauge.css'

const money = (minor: number, currency: string) => new Intl.NumberFormat(undefined, {
  style: 'currency', currency,
}).format(minor / 100)
const tickMoney = (minor: number, currency: string) => new Intl.NumberFormat(undefined, {
  style: 'currency', currency, minimumFractionDigits: minor % 100 === 0 ? 0 : 2, maximumFractionDigits: 2,
}).format(minor / 100)
const amount = (value: number | null) => value != null && Number.isFinite(value) && value >= 0 ? value : null

/** Paid readings anchor at the accepted minimum; only a free trial has a true free baseline. */
export default function PackageCostGauge({ summary, headingId }: { summary: PackageSummary; headingId: string }) {
  const gradientId = `cost-gauge-${useId().replace(/:/g, '')}`
  const { billing, package: pkg } = summary
  const floor = amount(billing.effective_floor_minor)
  const candidateCap = amount(billing.effective_cap_minor)
  const cap = billing.maximum_kind !== 'uncapped' && billing.maximum_kind !== 'unavailable'
    && floor != null && candidateCap != null && candidateCap >= floor ? candidateCap : null
  const minimumPosition = floor != null && cap != null && cap > 0 ? floor / cap : null
  const minimumAngle = (minimumPosition ?? 0) * Math.PI
  const showMinimumTick = minimumPosition != null && minimumPosition > 0 && minimumPosition < 1
  const trial = summary.state === 'trial' && floor === 0 && cap === 0
  const spend = amount(billing.current_spend_minor)
  // An inconsistent paid reading is pending, not permission to invent a different bill.
  const current = summary.state === 'paid' && spend != null && floor != null && spend < floor ? null : spend
  const position = current != null && cap != null && cap > 0
    ? Math.max(minimumPosition ?? 0, Math.min(1, current / cap)) : null
  const displayPosition = position ?? (trial ? 0 : minimumPosition ?? 0)
  const minimumBilled = !trial && floor != null && floor > 0 && current != null && current <= floor
  const fixedPrice = current != null && floor === cap && current === floor
  const muted = current == null || (position == null && !fixedPrice)
  const maximumLabel = billing.maximum_kind === 'estimated_maximum' ? 'Estimated maximum' : 'Maximum'
  const maximumValue = billing.maximum_kind === 'uncapped' ? 'No limit' : cap == null ? 'Pending' : money(cap, pkg.currency)
  const valueDescription = `${current == null ? 'Gathering usage; current spend is not ready yet' : `Current spend ${money(current, pkg.currency)}`}. Minimum ${floor == null ? 'pending' : money(floor, pkg.currency)}. ${maximumLabel} ${maximumValue}.`

  return <div className="package-cost-visual">
    <div className={`package-cost-gauge${muted ? ' is-pending' : ''}${minimumBilled ? ' is-at-minimum' : ''}${trial ? ' is-trial' : ''}`}
      role={position == null ? 'group' : 'meter'}
      aria-label={position == null ? valueDescription : 'Current server spend toward monthly maximum'}
      aria-valuemin={position == null ? undefined : 0}
      aria-valuemax={position == null || cap == null ? undefined : cap / 100}
      aria-valuenow={position == null || cap == null || current == null ? undefined : Math.min(cap, current) / 100}
      aria-valuetext={position == null ? undefined : valueDescription}
    >
      <svg viewBox="0 0 320 204" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="40" y1="0" x2="280" y2="0" gradientUnits="userSpaceOnUse">
            <stop stopColor="#a78bfa" />
            <stop offset="1" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <g key={`${current}:${floor}:${cap}`} className="package-cost-gauge-ticks">
          {Array.from({ length: 13 }, (_, index) => {
            const angle = (index / 12) * Math.PI
            if (showMinimumTick && Math.abs(index / 12 - minimumPosition!) < 0.001) return null
            return <line key={index}
              x1={160 - Math.cos(angle) * 128} y1={140 - Math.sin(angle) * 128}
              x2={160 - Math.cos(angle) * (index === 12 ? 140 : 134)} y2={140 - Math.sin(angle) * (index === 12 ? 140 : 134)}
              style={{ animationDelay: `${index * 20}ms` }} className={`package-cost-gauge-tick${minimumPosition != null && index / 12 < minimumPosition ? ' is-minimum-zone' : ''}${index === 12 && !trial ? ' is-maximum' : ''}${index === 0 && trial ? ' is-free-anchor' : ''}`} />
          })}
        </g>
        <path className="package-cost-gauge-track" d="M 50 140 A 110 110 0 0 1 270 140" />
        {showMinimumTick && <path className="package-cost-gauge-minimum-zone" d="M 50 140 A 110 110 0 0 1 270 140" pathLength="100"
          strokeDasharray={`${minimumPosition! * 100} 100`} />}
        {showMinimumTick && <g className="package-cost-gauge-minimum-mark" data-position={minimumPosition}>
          <title>Usage up to {money(floor!, pkg.currency)} is billed at the minimum.</title>
          <line x1={160 - Math.cos(minimumAngle) * 122} y1={140 - Math.sin(minimumAngle) * 122}
            x2={160 - Math.cos(minimumAngle) * 140} y2={140 - Math.sin(minimumAngle) * 140} />
          <circle cx={160 - Math.cos(minimumAngle) * 140} cy={140 - Math.sin(minimumAngle) * 140} r="2.5" />
          <text x={Math.max(42, Math.min(278, 160 - Math.cos(minimumAngle) * 151))}
            y={Math.max(16, 136 - Math.sin(minimumAngle) * 151)} textAnchor="middle">MIN {tickMoney(floor!, pkg.currency)}</text>
        </g>}
        <g className="package-cost-gauge-scale-labels">
          <text x="36" y="162" textAnchor="middle" className={trial ? 'package-cost-gauge-free-label' : ''}>{trial ? 'FREE' : 'Low usage'}</text>
          {!trial && <>
            <text x="278" y="158" textAnchor="middle" className="package-cost-gauge-maximum-label">{billing.maximum_kind === 'uncapped' ? 'NO LIMIT' : cap == null ? 'MAX —' : `${billing.maximum_kind === 'estimated_maximum' ? 'EST' : 'MAX'} ${tickMoney(cap, pkg.currency)}`}</text>
            <text x="278" y="172" textAnchor="middle">{cap == null ? 'Range pending' : 'Always online'}</text>
          </>}
        </g>
        <path className="package-cost-gauge-fill" d="M 50 140 A 110 110 0 0 1 270 140" pathLength="100"
          stroke={`url(#${gradientId})`} strokeDasharray="100 100" style={{ strokeDashoffset: 100 - (trial && current != null ? 0.0125 : displayPosition) * 100 }} />
        <g className="package-cost-gauge-needle" style={{ transform: `rotate(${displayPosition * 180}deg)` }}>
          <path d="M 160 135 L 79 140 L 160 145 Z" />
        </g>
        <circle cx="160" cy="140" r="7" className="package-cost-gauge-pivot" />
        <g className="package-cost-gauge-bubble-orbit" data-position={displayPosition} style={{ transform: `rotate(${displayPosition * 180}deg)` }}>
          <circle cx="50" cy="140" r="11" className="package-cost-gauge-bubble-glow" />
          <circle cx="50" cy="140" r="6" className="package-cost-gauge-bubble" />
        </g>
      </svg>
      <h2 id={headingId} className="package-cost-gauge-reading">
        <span>{trial ? 'Free right now' : 'Current spend'}</span>
        <strong className={current == null ? 'is-calculating' : ''} aria-live="polite">{current == null ? 'Gathering usage' : money(current, pkg.currency)}</strong>
      </h2>
    </div>
    {current != null && <BillingUsageFreshness usageAsOf={billing.usage_as_of} asOf={summary.as_of} period={billing.period} packageSummaries={[summary]} active={['paid', 'trial', 'legacy_grant'].includes(summary.state)} className="text-center" />}
    {current != null && cap != null && current > cap && <p className="package-cost-gauge-notice" role="status">Current spend exceeds this server’s maximum. Contact support.</p>}
  </div>
}
